import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, Subject, concat, of} from 'rxjs';
import {Scadenza} from '../../../../common/models/scadenza';
import {catchError, distinctUntilChanged, map, mergeMap, skipWhile, switchMap, take, takeLast, tap} from 'rxjs/operators';
import {Petitum} from '../../../../common/models/petitum';
import {AltraParte} from 'src/app/common/models/altraparte';
import {TipoFascicolo} from '../../../../common/models/tipologiche/tipo-fascicolo';
import {Classificazione} from '../../../../common/models/classificazione';
import {Controparte} from '../../../../common/models/controparte';
import {Step} from '../models/step';
import {FascicoloTemp, Valore} from '../models/fascicolo-temp';
import {CreazionePraticaService} from './creazione-pratica.service';
import {PraticaLight} from '../../../../common/models/pratica/pratica-light';
import {TipoPratica} from '../../../../common/models/tipologiche/tipo-pratica';
import {ElaborationDocumentFilterEntity} from '../../../../document-management/models/elaboration-doc-filter';
import {DocumentoAllegato} from '../../../../common/models/documento-allegato';
import {DocumentManagementService} from '../../../../document-management/services/document-management.service';
import {Atto} from 'src/app/common/models/atto';
import {AsyncValidatorFn, FormArray, FormBuilder, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {PropertiesSettings} from '../../../../core/configuration/properties-settings';
import {Parte} from '../../../../common/models/parte';
import {PraticaPenale} from "../../../../common/models/pratica/pratica-penale";
import { isPetitumValid } from '../utils/petitum-utils';
import { getValidationFascicolo } from '../utils/fascicolo-utils';
import { SuggestedParte, SuggestedSoggettiTerzi } from '../models/suggested';
import { FascicoloFull } from 'src/app/common/models/fascicolo/fascicolo-full';


@Injectable({
  providedIn: 'root'
})
export class CreazionePraticaStoreService {

  fromWizard = false;

  readonly PROPERTIES_SETTINGS = PropertiesSettings;

  private idElaborazioneBS$: BehaviorSubject<string> = new BehaviorSubject<string>('');

  //PDA
  private codiceRegistroBS$: BehaviorSubject<string> = new BehaviorSubject<string>('');

  private sectionLoad: BehaviorSubject<any> = new BehaviorSubject(null);
  sectionLoad$: Observable<any> = this.sectionLoad.asObservable();

  private fascicoloLoad: BehaviorSubject<any> = new BehaviorSubject(null);
  fascicoloLoad$: Observable<any> = this.fascicoloLoad.asObservable();

  private docAdd: BehaviorSubject<any> = new BehaviorSubject(null);
  _docAdd: Observable<any> = this.docAdd.asObservable();

  private praticaBS$: BehaviorSubject<PraticaLight | null | PraticaPenale> = new BehaviorSubject<PraticaLight | null | PraticaPenale>(null);
  pratica$: Observable<PraticaLight | null | PraticaPenale> = this.praticaBS$.asObservable();

  private classificazioniBS$: BehaviorSubject<Classificazione[] | null> = new BehaviorSubject<Classificazione[] | null>(null);
  classificazioni$: Observable<Classificazione[] | null> = this.classificazioniBS$.asObservable();

  private tipoPraticaBS$: BehaviorSubject<TipoPratica> = new BehaviorSubject<TipoPratica>(null);
  tipoPratica$: Observable<TipoPratica> = this.tipoPraticaBS$.asObservable();

  private tipoFascicoloBS$: BehaviorSubject<TipoFascicolo> = new BehaviorSubject<TipoFascicolo>(null);
  tipoFascicolo$: Observable<TipoFascicolo> = this.tipoFascicoloBS$.asObservable();

  private attiBS$: BehaviorSubject<Atto[]> = new BehaviorSubject<Atto[]>([]);
  atti$: Observable<Atto[]> = this.attiBS$.asObservable();

  private scadenzeBS$: BehaviorSubject<Scadenza[]> = new BehaviorSubject<Scadenza[]>([]);
  scadenze$: Observable<Scadenza[]> = this.scadenzeBS$.asObservable();

  private soggettoTerzoBS$: BehaviorSubject<AltraParte[]> = new BehaviorSubject<AltraParte[]>([]);
  soggettoTerzo$: Observable<AltraParte[]> = this.soggettoTerzoBS$.asObservable();

  private entitiesLoaded = false;
  private entitiesBS$: BehaviorSubject<ElaborationDocumentFilterEntity[]> = new BehaviorSubject<ElaborationDocumentFilterEntity[]>([]);
  entities$: Observable<ElaborationDocumentFilterEntity[]> = this.entitiesBS$.asObservable().pipe(
    distinctUntilChanged(),
  );

  private fascicoloBS$: BehaviorSubject<any | null> = new BehaviorSubject<any | null>(null);
  fascicolo$: Observable<any> = this.fascicoloBS$.asObservable();

  private stepListLoaded = false;
  private stepListBS$: BehaviorSubject<Step[]> = new BehaviorSubject<Step[]>([]);

  stepList$: Observable<Step[]> = this.stepListBS$.asObservable().pipe(
    distinctUntilChanged(),
    take(1),
    switchMap(() => this.creazionePraticaService.getStepList()),
    tap((value) => {
      if(!this.stepListLoaded) {
        this.stepListBS$.next(value.steps);
        this.stepListLoaded = true;
      }
      if (!this.currentStepBS$.value) {
        this.setCurrentStep(1);
      }
    }),
    map((value: { steps: Step[] }) => value.steps),
  );

  private fascicoloTempIdBS$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  fascicoloTempID$: Observable<string | null> = this.fascicoloTempIdBS$.asObservable();

  currentStepBS$: BehaviorSubject<Step | null> = new BehaviorSubject<Step | null>(null);
  currentStep$: Observable<Step | null> = this.currentStepBS$.asObservable();


  private petitumBS$: BehaviorSubject<Petitum | null> = new BehaviorSubject<Petitum | null>(null);
  petitum$: Observable<Petitum | null> = this.petitumBS$.asObservable()

  private contropartiBS$: BehaviorSubject<Controparte[]> = new BehaviorSubject<Controparte[]>(null);
  controparti$: Observable<Controparte[]> = this.contropartiBS$.asObservable();

  private elaborationLoadedBS$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  elaborationLoaded$: Observable<boolean> = this.elaborationLoadedBS$.asObservable();

  private documentazioneElaborazioneLoaded = false;
  private documentazioneElaborazioneBS$: BehaviorSubject<DocumentoAllegato[]> = new BehaviorSubject<DocumentoAllegato[]>([]);
  documentazioneElaborazione$ = this.documentazioneElaborazioneBS$.asObservable()
    .pipe(
      distinctUntilChanged(),
      tap(() => {
        //console.log('[documentazioneElaborazione$]', '||', '---')
        if (!this.documentazioneElaborazioneLoaded && this.idElaborazioneBS$.value && this.idElaborazioneBS$.value !== 'new') {
          this.documentManagementService.getDocuments(this.idElaborazioneBS$.value).subscribe(it => {
            console.log('[documentazioneElaborazione$]', '||', 'load', it)
            this.documentazioneElaborazioneLoaded = true;
            this.documentazioneElaborazioneBS$.next(it.documenti);
          })
        }
      })
    )

  private documentazioneAllegataBS$: BehaviorSubject<DocumentoAllegato[]> = new BehaviorSubject<DocumentoAllegato[]>([]);
  documentazioneAllegata$ = this.documentazioneAllegataBS$.asObservable()
    .pipe(
      distinctUntilChanged()
    )

  private fascicoloTempBS$: BehaviorSubject<Valore> = new BehaviorSubject<Valore>(this.buildRequest().fascicoloTemp.valore);
  fascicoloTemp$: Observable<Valore> = this.fascicoloTempBS$.asObservable();

  private fascicoloCreatoBS$: Subject<FascicoloTemp | null> = new Subject<FascicoloTemp | null>();
  fascicoloCreato$: Observable<FascicoloTemp | null> = this.fascicoloCreatoBS$.asObservable();

  private praticaValidBS$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private fascicoloValidBS$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private petitumValidBS$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private checkValidityBS$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  checkValidity$: Observable<boolean> = this.checkValidityBS$.pipe(
    map(() => {
    return this.isValid(this.fascicoloTempBS$.value);
  }))

  private saveExitFascicoloEventBS$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  saveExitFascicoloEvent$: Observable<boolean> = this.saveExitFascicoloEventBS$.asObservable();

  private suggestedPartiBS$: BehaviorSubject<SuggestedParte[]> = new BehaviorSubject<SuggestedParte[]>([]);
  suggestedParti$: Observable<SuggestedParte[]> = this.suggestedPartiBS$.asObservable();

  private suggestedSoggettiTerziBS$: BehaviorSubject<SuggestedSoggettiTerzi[]> = new BehaviorSubject<SuggestedSoggettiTerzi[]>([]);
  suggestedSoggettiTerzi$: Observable<SuggestedSoggettiTerzi[]> = this.suggestedSoggettiTerziBS$.asObservable();



  constructor(
    private creazionePraticaService: CreazionePraticaService,
    private documentManagementService: DocumentManagementService,
    private fb: FormBuilder
  ) {
  }

  setCurrentStep(numeroStep: number) {
    this.currentStepBS$.next(this.stepListBS$.value.find(step => step.numeroStep === numeroStep));
  }

  setEntities(entities: ElaborationDocumentFilterEntity[]) {
    this.entitiesBS$.next(entities);
  }

  setElaborazione(idElaborazione: string) {
    console.log(idElaborazione);
    this.idElaborazioneBS$.next(idElaborazione);
    if(idElaborazione !== 'new'){
      return this.creazionePraticaService.getFascicoloTemp(idElaborazione)
      .pipe(tap(el => {
        this.updateAll(el);
        this.elaborationLoadedBS$.next(true);
      }));
    }
  }

  setCodiceRegistro(codiceRegistro: string) {
    this.codiceRegistroBS$.next(codiceRegistro);
  }

  /* CLASSIFICAZIONI */
  checkClassificazioni(): Observable<boolean> {
    return this.classificazioni$.pipe(
      skipWhile(el => !el),
      map(classification => classification ?
        classification.filter(el => el.isPrincipale).length > 0 : false)
    );
  }

  /*addClassificazioni(classificazione: Classificazione) {
    if(!this.classificazioniBS$.value){
      this.classificazioniBS$.next([]);
    }
    if (this.classificazioniBS$.value.length === 0) {
      classificazione.isPrincipale = true;
    }
    if (classificazione.isPrincipale) {
      this.classificazioniBS$.value.forEach(item => item.isPrincipale = false)
    }
    this.classificazioniBS$.next([...this.classificazioniBS$.value, classificazione]);
    this.updateFascicoloTemp('classificazione', {classificazioni: this.classificazioniBS$.value})
  }

  removeClassificazione(classificazione: Classificazione) {
    const newValue = this.classificazioniBS$.value.filter(el => el !== classificazione);
    this.classificazioniBS$.next(newValue.length > 0 ? newValue : null);
    this.updateFascicoloTemp('classificazione', {classificazioni: this.classificazioniBS$.value})
  }*/

  setClassificazioni(classificazioni: Classificazione[]) {
    this.classificazioniBS$.next(classificazioni);
    this.updateFascicoloTemp('classificazione', {classificazioni: this.classificazioniBS$.value})
  }

  /* CLASSIFICAZIONI */

  /* ATTI */
  addAtto(atto: Atto) {
    this.attiBS$.next([...this.attiBS$.value, atto]);
    this.updateFascicoloTemp('atti', this.attiBS$.value);
  }

  setAtti(atti: Atto[]) {
    this.attiBS$.next(atti);
    this.updateFascicoloTemp('atti', this.attiBS$.value);
  }

  /* ATTI */

  /* PRATICA */
  setTipoPratica(tipoPratica: TipoPratica) {
    if(!this.praticaBS$.value){
      this.praticaBS$.next(new PraticaLight());
    }
    this.tipoPraticaBS$.next(tipoPratica);
    const pratica = this.praticaBS$.value;
    pratica.tipoPratica = tipoPratica;
    this.praticaBS$.next(pratica);
    this.updateFascicoloTemp('pratica', this.praticaBS$.value);
  }

  onSectionLoaded(param: any) {
    this.sectionLoad.next(param);
  }

  onFascicoloLoad(fascicolo: FascicoloFull) {
    this.fascicoloLoad.next(fascicolo);
  }

  onDocAdded(param) {
    this.docAdd.next(param);
  }

  updatePratica(pratica: PraticaLight, tipoPratica: TipoPratica, classificazioni: Classificazione[]) {
    this.setClassificazioni(classificazioni);
    this.setTipoPratica(tipoPratica);
    this.praticaBS$.next({...this.praticaBS$.value, ...pratica});
    this.updateFascicoloTemp('pratica', this.praticaBS$.value);
  }

  getPratica(): PraticaLight {
    return this.praticaBS$.value;
  }

  updateValidityPratica(isValid: boolean){
    this.praticaValidBS$.next(isValid);
    this.checkValidityBS$.next(null);
  }

  /* PRATICA */

  /* FASCICOLO */
  setTipoFascicolo(tipoFascicolo: TipoFascicolo) {
    this.tipoFascicoloBS$.next(tipoFascicolo);
  }

  setFascicolo(fascicolo: any) {
    this.fascicoloBS$.next(fascicolo);
    this.updateFascicoloTemp('fascicolo', this.fascicoloBS$.value)
  }

  updateFascicolo(fascicolo: any) {
    if(fascicolo) {
      this.fascicoloBS$.next({ ...this.fascicoloBS$.value, ...fascicolo });
      this.updateFascicoloTemp('fascicolo', this.fascicoloBS$.value)
    }
  }

  getFascicoloTemp ()  {
    return this.fascicoloTempBS$.value;
  }

  getTipoFascicolo(): TipoFascicolo {
    return this.tipoFascicoloBS$.value;
  }
  getTipoPratica(): TipoPratica {
    return this.tipoPraticaBS$.value;
  }

  updateFascicoloTemp(key: string, value: any) {
    const fascicoloTemp = this.fascicoloTempBS$.value;
    fascicoloTemp[key] = value;
    this.fascicoloTempBS$.next(fascicoloTemp);
    this.checkValidityBS$.next(null);
    // console.log('[FASCICOLO TEMP]',this.fascicoloTempBS$.value);
  }

  updateValidityFascicolo(isValid: boolean){
    this.fascicoloValidBS$.next(isValid);
    this.checkValidityBS$.next(null);
  }

  updateValidityPetitum(isValid: boolean){
    this.petitumValidBS$.next(isValid);
    this.checkValidityBS$.next(null);
  }

  /* FASCICOLO */

  /* PETITUM */

  setPetitum(petitum: Petitum) {
    // console.log('[setPetitum]', petitum);
    this.petitumBS$.next(petitum);
    this.updateFascicoloTemp('petitum', this.petitumBS$.value)
  }

  /* PETITUM */

  /* ADEMPIMENTI */

  setAdempimenti(scadenze: Scadenza[]) {
    this.scadenzeBS$.next(scadenze);
    this.updateFascicoloTemp('adempimenti', this.scadenzeBS$.value)
  }

  /* ADEMPIMENTI */


  /* SOGGETTI TERZI */

  setSoggettiTerzi(soggetti: AltraParte[]) {
    this.soggettoTerzoBS$.next(soggetti);
    this.updateFascicoloTemp('altreParti', this.soggettoTerzoBS$.value);
  }

  /* SOGGETTI TERZI */

  /* CONTROPARTI */

  setControparti(controparti: Controparte[]) {
    this.contropartiBS$.next((controparti && controparti.length > 0) ? controparti : null);
    this.updateFascicoloTemp('controparti', this.contropartiBS$.value);
  }

  /* CONTROPARTI */

  /* DOCUMENTAZIONE ALLEGATA */

  setDocumentazioneAllegata(documenti: DocumentoAllegato[]) {
    // console.log('[setDocumentazioneAllegata]', documenti);
    this.documentazioneAllegataBS$.next(documenti);
    this.updateFascicoloTemp('documentazioneAllegata', this.documentazioneAllegataBS$.value)
  }

  /* DOCUMENTAZIONE ALLEGATA */

  /* UTILITY */

  hasStepData(numStep): boolean {
    switch(numStep){
      case 1:
        return this.isValidStep(1);
      case 2:
        return this.isValidStep(2);
      case 3:
        return this.isValidStep(3);
      case 4:
        return (this.soggettoTerzoBS$.value != null && this.soggettoTerzoBS$.value.length != 0);
      case 5:
        return this.isValidStep(5);
      case 6:
        return (this.attiBS$.value != null && this.attiBS$.value.length != 0);
      case 7:
        return (this.scadenzeBS$.value != null && this.scadenzeBS$.value.length != 0);
      case 8:
        return (this.documentazioneAllegataBS$.value != null && this.documentazioneAllegataBS$.value.length != 0);
    }
  }

  checkShowCompleta(): Observable<boolean>{
    return new Observable(subscriber => {
      subscriber.next(this.isValid(this.fascicoloTempBS$.value))
    })
  }

  isValid(valore: Valore): boolean {
    const form = this.parseData(valore, this.getValidationSchema()) as FormGroup;
    //console.log('[Fascicolo valid]', this.fascicoloValidBS$.value);
    //console.log('[Pratica valid]',this.praticaValidBS$.value);
    //console.log('[Petitum valid]',this.petitumValidBS$.value);
    return form.valid && this.fascicoloValidBS$.value && this.isValidStep(1) && this.petitumValidBS$.value;
  }

  isValidStep(numeroStep): boolean {
    let form : FormGroup;
    let step;
    if(numeroStep == 1) return this.praticaValidBS$.value && (this.classificazioniBS$.value != null && this.classificazioniBS$.value.length > 0);
    if (numeroStep == 2) return this.fascicoloValidBS$.value;
    if(numeroStep == 3) return this.contropartiBS$.value != null && this.contropartiBS$.value.length > 0;
    //(numeroStep == 5)? step = this.petitumBS$.value : '';
    if(numeroStep == 5) return this.petitumValidBS$.value;
    if(numeroStep == 4 || numeroStep > 5) return true;
  }

  private parseData(data: any, validators?: any): FormGroup | FormArray | [any, ValidatorFn[], AsyncValidatorFn[]] {
    if (Array.isArray(data)) {
      return this.fb.array(data.map((item, index) => this.parseData(item, (validators || {})[index])));
    }
    if (typeof data === 'object' && data !== null) {
      const formGroupContent = {};
      for (const [key, value] of Object.entries(data)) {
        formGroupContent[key] = this.parseData(value, (validators || {})[key]);
      }
      return this.fb.group(formGroupContent);
    }
    const [syncValidators = [], asyncValidators = []] = validators || [];
    return [data, syncValidators, asyncValidators];
  }

  /* UTILITY */

  /* OPERATIONS */

  buildRequest(): FascicoloTemp {
    const req = new FascicoloTemp();

    req.fascicoloTemp = {
      id: this.fascicoloTempIdBS$.value,
      step: this.currentStepBS$.value,
      valore: {
        pratica: this.praticaBS$.value,
        classificazione: {
          classificazioni: this.classificazioniBS$.value
        },
        petitum: this.petitumBS$.value,
        fascicolo: this.fascicoloBS$.value,
        controparti: this.contropartiBS$.value,
        altreParti: this.soggettoTerzoBS$.value,
        atti: this.attiBS$.value,
        adempimenti: this.scadenzeBS$.value,
        documentazioneAllegata: this.documentazioneAllegataBS$.value
      }
    }
    return req;
  }

  updateAll(fascicoloTemp: FascicoloTemp) {
    console.log('[FASCICOLO TEMP]', fascicoloTemp);
    if (fascicoloTemp.fascicoloTemp) {
      const {id, valore} = fascicoloTemp.fascicoloTemp;

      this.fascicoloTempIdBS$.next(id);

      if (valore.pratica) {
        this.praticaValidBS$.next(valore.pratica.tipoPratica && (valore.pratica.tipoPratica.label != 'PENALE' || (valore.pratica.tipoPratica.label == 'PENALE' && (valore.pratica as PraticaPenale).tipoReato != null)));
        this.updatePratica(valore.pratica, valore.pratica.tipoPratica, null);
      }

      if (valore.classificazione) {
        this.setClassificazioni(valore.classificazione.classificazioni ? valore.classificazione.classificazioni : []);
      }

      if (valore.fascicolo) {
        if(valore.fascicolo.tipoFascicolo){
          const form = this.parseData(valore.fascicolo, getValidationFascicolo(valore.fascicolo.tipoFascicolo)) as FormGroup;
          this.updateValidityFascicolo(form.valid);
        }
        this.updateFascicolo(valore.fascicolo);
        this.setTipoFascicolo(valore.fascicolo.tipoFascicolo);
      }

      this.setAtti(valore.atti);

      this.petitumValidBS$.next(isPetitumValid(valore.petitum))

      this.setPetitum(valore.petitum);

      this.setSoggettiTerzi(valore.altreParti);

      this.setAdempimenti(valore.adempimenti);

      this.setControparti(valore.controparti);

      this.setDocumentazioneAllegata(valore.documentazioneAllegata);
    }
  }

  deleteAll(isDeleteFascicoloTemp: boolean = true) {

    if(isDeleteFascicoloTemp) {
      console.log('[deleteAll]');
      this.elaborationLoadedBS$.next(false);
      this.idElaborazioneBS$.next('');
      this.fascicoloTempIdBS$.next(null);
      this.currentStepBS$.next(null);

      this.praticaValidBS$.next(null);
      this.fascicoloValidBS$.next(null);
      this.petitumValidBS$.next(null);
      this.setEntities([]);
    }

    this.fascicoloCreatoBS$.next(null);

    this.fascicoloBS$.next(null);

    this.setTipoPratica(null);
    this.praticaBS$.next(null);
    this.updateValidityPratica(false);

    this.setClassificazioni([]);

    this.setTipoFascicolo(null);
    this.updateFascicolo(null);
    this.updateValidityFascicolo(false);

    this.setAtti([]);

    this.setPetitum(null);
    this.updateValidityPetitum(false);

    this.setSoggettiTerzi([]);

    this.setAdempimenti([]);

    this.setControparti([]);

    this.documentazioneElaborazioneLoaded = false;
    this.documentazioneElaborazioneBS$.next([]);
    this.setDocumentazioneAllegata([]);

    this.suggestedPartiBS$.next([]);
    this.suggestedSoggettiTerziBS$.next([]);

  }

  async save() {
    if (this.idElaborazioneBS$.value !== '' && this.idElaborazioneBS$.value !== 'new') {
    await this.creazionePraticaService
      .updateFascicoloTemp(this.buildRequest(), this.idElaborazioneBS$.value)
      .pipe(
        tap((el: FascicoloTemp) => this.updateAll(el))
      )
      .toPromise()
      .then();
    }
  }


  //eseguo: 1.update fascicoloTemp
  //        2.save fascicoloTemp
  //        3.associazione fascicolo-elaborazione
  //        4.salvo fascicolo creato (provoca anche la chiusura della modale)
  completa() {
    let fascicolo = null;
    let update$ = null;
    if(this.idElaborazioneBS$.value !== 'new'){
      update$ = this.creazionePraticaService.updateFascicoloTemp(this.buildRequest(), this.idElaborazioneBS$.value)
      const save$ = this.creazionePraticaService.completaFascicoloTemp(this.buildRequest(), this.idElaborazioneBS$.value);
      const associa$ = (idFascicolo) => this.creazionePraticaService.associaFascicolo(this.idElaborazioneBS$.value, idFascicolo)

      concat(update$, save$).pipe(
        takeLast(1),
        mergeMap((fascicoloCreato: any) => {
          fascicolo = fascicoloCreato.fascicolo;
          return associa$(fascicoloCreato.fascicolo.id);
        }),
      ).subscribe(() => this.fascicoloCreatoBS$.next(fascicolo),
                  () => this.fascicoloCreatoBS$.next(null));
    } else {
      update$ = this.creazionePraticaService.updateFascicoloTempPDA(this.buildRequest(), this.idElaborazioneBS$.value);

      const save$ = update$.pipe(
        tap((res: any) => {
          this.idElaborazioneBS$.next(res.idElaborazione);
          this.fascicoloTempIdBS$.next(res.fascicoloTemp.id);
        }),
        mergeMap((res: any) =>
          this.creazionePraticaService.completaFascicoloTempPDA(this.buildRequest(), res.idElaborazione)),
        mergeMap((res : any) => {
          fascicolo = res.fascicolo;
          return this.creazionePraticaService.createFascicoloPct(this.buildRequest(), fascicolo.id, this.codiceRegistroBS$.value)
        })
      );

      save$.subscribe(() => this.fascicoloCreatoBS$.next(fascicolo),
                      () => this.fascicoloCreatoBS$.next(null));
    }

  }



  deleteFascicoloTemp() {
    return this.creazionePraticaService.deleteFascicoloTemp(this.idElaborazioneBS$.value)
      .pipe(
        tap(() => {
          this.deleteAll();
        })
      );
  }

  /* OPERATIONS */

  getValidationSchema() {
    return {
      pratica: [Validators.required],
      classificazione: {
      classificazioni: [Validators.required, Validators.minLength(1)]
      },
      petitum: [Validators.required],
      //fascicolo: getValidationFascicolo(this.tipoFascicoloBS$.value),
      controparti: [Validators.required, Validators.minLength(1)],
    };
  }

  setSaveExitEvent(value: boolean) {
    this.saveExitFascicoloEventBS$.next(value);
  }

  //salvo parti suggerite
  setSuggestedParti(parti: SuggestedParte[]) {
    if(parti.length > 0) {
      this.suggestedPartiBS$.next(parti);
    }
  }

  setSuggestedSoggettiTerzi(soggettiTerzi: SuggestedSoggettiTerzi[]) {
    if (soggettiTerzi.length > 0) {
      this.suggestedSoggettiTerziBS$.next(soggettiTerzi);
    }
  }

}
